import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs/operators';

import { AuthService } from '@shared/modules/auth/services/auth.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { ResizeService } from './resize.service';
import { Telemetry } from '../interfaces/telemetry';
import { TelemetryDataService } from './telemetry-data.service';
import { WINDOW } from './window.service';

import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {

  injectedElements: any = {}; // Elements that we've injected
  isCustomer = false; // Is the user a customer?
  isEmployee = false; // Is the user an employee?

  // We can't inject AuthService through constructor because AuthService uses TelemetryService already.
  private authService: AuthService | null = null;
  private lastPageLoad: string | null = null;
  private previousPath: string | null = null;
  private screenSize = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private injector: Injector,
    private localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: object,
    private resizeService: ResizeService,
    private router: Router,
    private telemetryDataService: TelemetryDataService,
    @Inject(WINDOW) private window: Window | null
  ) {

    // Get the current screen size
    this.resizeService.onResize.subscribe(size => this.screenSize = size);

    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          const nav = event as NavigationEnd;
          this.trackPageLoad(nav.urlAfterRedirects, this.previousPath);
          this.previousPath = this.shortenUrl(this.stripHost(nav.urlAfterRedirects));
        });
    }
  }

  inject(event: string, elementId: string, sourcePath: string, onlyCustomer: boolean) {

    // Get the element(s) to inject
    const querySelector = '[id^="' + elementId + '"]';
    const elements = isPlatformBrowser(this.platformId) ? this.document.querySelectorAll(querySelector) : [];

    // First check that we can inject this element!
    if (this.canInject(onlyCustomer)) {

      elements.forEach((element: any) => {
        if (this.injectedElements[element.id]) {
          return;
        }

        // Set the payload (Telemetry) object to the element
        // Remove any query params from the sourcePath
        element.payload = <Telemetry>{
          ClientId: this.localStorageService.getItem('CLIENT'),
          DestinationPath: this.shortenUrl(element.getAttribute('tm-destination')),
          Element: element.id,
          Event: event,
          SourcePath: this.shortenUrl(sourcePath || this.router.url),
          UserAgent: this.window && (this.window.navigator.userAgent || this.window.navigator.vendor),
          UserId: this.getUserId()
        };

        // Set the event listener based on the Telemetries event
        // Currently only CLICK is supported, but other event types could be added in the future
        switch (event) {
          case 'CLICK':

            // Add the event listener
            element.addEventListener('click', this.handleClick, false);

            // Add the injected element into our hash for reference
            // (e.g. we may need to remove event listeners when staff logs in)
            const injectedElement = { element: element, type: 'click', listener: this.handleClick };
            this.injectedElements[element.id] = injectedElement;
            break;
        }
      });
    }
  }

  remove() {

    // For each of the injected elements, remove the listener that we've added
    Object.values(this.injectedElements).forEach((injectedElement: any) => {
      injectedElement.element.removeEventListener(injectedElement.type, injectedElement.listener);
    });

    // Clear the array of injected elements
    this.injectedElements = {};
  }

  setIsCustomer(isCustomer: boolean) {
    this.isCustomer = isCustomer;
  }

  setIsEmployee(isEmployee: boolean) {
    this.isEmployee = isEmployee;
  }

  // Can we inject the element?
  private canInject(onlyCustomer: boolean): boolean {
    return !this.isEmployee || !onlyCustomer;
  }

  private getReferrer(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }

    const referrer = this.document.referrer || '';
    return referrer ? referrer.substring(0, 255) : '';
  }

  private getUserId(): number | null {
    try {
      this.authService = this.authService || this.injector.get(AuthService);
      return this.authService.getUserId() || null;
    } catch {
      return null;
    }
  }

  // Click callback
  private handleClick = (event: Event) => {

    const target = event.currentTarget as HTMLElement;

    // Get the payload object from the element
    const payload = (target as any).payload;

    // Set the current screen size
    payload.ScreenSize = this.screenSize;

    // If this is an anchor element, stop the default navigation and handle it after telemetry
    let href: string | null = null;
    let anchorTarget: string | null = null;
    if (target && target.tagName === 'A' && target.getAttribute('href')) {
      event.preventDefault();
      event.stopPropagation();
      href = target.getAttribute('href');
      anchorTarget = (target as HTMLAnchorElement).target || null;
    }

    this.sendTelemetry(payload, () => {
      if (href && this.window) {
        if (anchorTarget) {
          this.window.open(href, anchorTarget);
        } else {
          this.window.location.href = href;
        }
      }
    });
  }

  private sendTelemetry(payload: Telemetry, onSuccess?: () => void) {

    if (!payload.ScreenSize) {
      payload.ScreenSize = this.screenSize;
    }

    if (!payload.UserAgent) {
      payload.UserAgent = this.window && (this.window.navigator.userAgent || this.window.navigator.vendor);
    }

    if (typeof payload.UserId === 'undefined') {
      payload.UserId = this.getUserId();
    }

    // Create the Telemetry record
    this.telemetryDataService.create(payload)
      .subscribe({
        next: () => onSuccess && onSuccess(),
        error: () => onSuccess && onSuccess() // Fail open so navigation is not blocked if telemetry errors
      });
  }

  private stripHost(value: string): string {
    if (!value) {
      return value;
    }

    try {
      const root = environment.urlRoot;
      if (root && value.startsWith(root)) {
        return value.substring(root.length) || '/';
      }
    } catch {
      // ignore
    }
    return value;
  }

  private shortenUrl(value: string): string {
    if (!value) {
      return '';
    }
    return value.length > 255 ? value.substring(0, 255) : value;
  }

  private trackPageLoad(destinationPath?: string, referrerOverride?: string) {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const targetPath = this.shortenUrl(destinationPath || this.router.url);

    // Avoid duplicate PAGE_LOAD events for the same path
    if (this.lastPageLoad === targetPath) {
      return;
    }

    const referrerRaw = referrerOverride || this.getReferrer();
    const referrer = this.shortenUrl(this.stripHost(referrerRaw) || targetPath);

    const payload: Telemetry = {
      ClientId: this.localStorageService.getItem('CLIENT'),
      DestinationPath: targetPath,
      Element: 'PAGE',
      Event: 'PAGE_LOAD',
      ScreenSize: this.screenSize,
      SourcePath: referrer,
      UserAgent: this.window && (this.window.navigator.userAgent || this.window.navigator.vendor),
      UserId: this.getUserId()
    };

    this.lastPageLoad = targetPath;
    this.sendTelemetry(payload);
  }
}
